import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

// Import services
import adminService from '../services/adminService';

// Import admin components 
import AdminStatsCard from '../components/AdminStatsCard';
import AdminTabNavigation from '../components/AdminTabNavigation';
import AdminCertificatesTab from '../components/AdminCertificatesTab';
import AdminCertificateVerificationTab from '../components/AdminCertificateVerificationTab';
import AdminUsersTab from '../components/AdminUsersTab';
import AdminRequestsTab from '../components/AdminRequestsTab';
import AdminListingsTab from '../components/AdminListingsTab';
import AdminReportsTab from '../components/AdminReportsTab';
import AdminContentTab from '../components/AdminContentTab';

// Loading spinner component
const Spinner = ({ size = 'md' }) => {
  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-8 w-8',
    lg: 'h-12 w-12'
  };

  return (
    <div className="flex justify-center">
      <div className={`animate-spin rounded-full border-b-2 border-teal-500 ${sizeClasses[size]}`}></div>
    </div>
  );
};

function AdminDashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [activeTab, setActiveTab] = useState('overview');

  // Check if the user is an admin, if not redirect to home
  useEffect(() => {
    if (user) {
      if (user.role !== 'admin') {
        toast.error('You do not have permission to access the admin dashboard');
        navigate('/');
      } else {
        console.log('[AdminDashboard] Admin user authenticated:', user.email);
      }
    }
  }, [user, navigate]);

  // Fetch dashboard stats
  useEffect(() => {
    const fetchDashboardStats = async () => {
      setLoading(true);
      try {
        // Try to get stats from the backend
        console.log('[AdminDashboard] Fetching dashboard data from API');
        
        let statsData;
        try {
          // First try to get data from the actual API endpoint
          const data = await adminService.getStatistics();
          statsData = data;
          console.log('[AdminDashboard] Successfully fetched stats from API:', data);
        } catch (apiError) {
          console.warn('[AdminDashboard] API fetch failed, falling back to dashboard data');
          
          try {
            // If stats endpoint fails, try the dashboard endpoint
            const dashboardData = await adminService.getDashboardData();
            statsData = dashboardData.stats;
            console.log('[AdminDashboard] Successfully fetched dashboard data:', dashboardData);
          } catch (dashboardError) {
            console.error('[AdminDashboard] Both API endpoints failed:', apiError, dashboardError);
            throw new Error('Failed to fetch dashboard data from both endpoints');
          }
        }
        
        // If we got data from either endpoint, use it
        if (statsData) {
          setStats(statsData);
        } else {
          // Fall back to mock data if both endpoints return invalid data
          console.warn('[AdminDashboard] Using mock data as fallback');
          setStats({
            totalDonations: 214,
            totalDonationsChange: '+12%',
            totalDonationsChangeType: 'positive',
            
            activeUsers: 78,
            activeUsersChange: '+5%',
            activeUsersChangeType: 'positive',
            
            pendingCertificates: 5,
            pendingCertificatesChange: '-2',
            pendingCertificatesChangeType: 'neutral',
            
            foodWasteSaved: 564,
            foodWasteSavedChange: '+48kg',
            foodWasteSavedChangeType: 'positive',
          });
        }
      } catch (err) {
        console.error('[AdminDashboard] Error fetching dashboard stats:', err);
        toast.error('Failed to load dashboard data. Using cached data if available.');
      } finally {
        setLoading(false);
      }
    };
    
    if (user && user.role === 'admin') {
      fetchDashboardStats();
    }
  }, [user]);

  // Handle tab change
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
  };

  // If not authenticated or not admin, don't render anything (redirect will happen)
  if (!user || user.role !== 'admin') {
    return null;
  }

  // Loading state
  if (loading && Object.keys(stats).length === 0) {
    return (
      <div className="min-h-screen flex justify-center items-start pt-16">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="py-6">
        <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
        <p className="mt-1 text-sm text-gray-500">
          Manage users, certificates, and monitor platform activity
        </p>
      </div>
      
      {/* Stats Section */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <Spinner size="lg" />
          <p className="ml-3 text-gray-600">Loading dashboard data...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <AdminStatsCard 
            title="Total Donations"
            value={stats.totalDonations || 0}
            change={stats.totalDonationsChange || '0%'}
            changeType={stats.totalDonationsChangeType || 'positive'}
            loading={false}
          />
          <AdminStatsCard 
            title="Active Users"
            value={stats.activeUsers || 0}
            change={stats.activeUsersChange || '0%'}
            changeType={stats.activeUsersChangeType || 'neutral'}
            loading={false}
          />
          <AdminStatsCard 
            title="Pending Certificates"
            value={stats.pendingCertificates || 0}
            change={stats.pendingCertificatesChange || '0'}
            changeType={stats.pendingCertificatesChangeType || 'neutral'}
            loading={false}
          />
          <AdminStatsCard 
            title="Food Waste Saved (kg)"
            value={stats.foodWasteSaved || 0}
            change={stats.foodWasteSavedChange || '0kg'}
            changeType={stats.foodWasteSavedChangeType || 'positive'}
            loading={false}
          />
        </div>
      )}
      
      {/* Tab Navigation */}
      
      <AdminTabNavigation 
        activeTab={activeTab}
        onTabChange={handleTabChange}
        counts={{
          pendingCertificates: stats.pendingCertificates || 0
        }}
      />
      
      {/* Tab Content */}
      <div className="mt-6">
        {/* Overview Tab Content */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <div className="bg-white shadow rounded-lg overflow-hidden">
              <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
                <h3 className="text-lg leading-6 font-medium text-gray-900">Platform Overview</h3>
                <p className="mt-1 max-w-2xl text-sm text-gray-500">
                  Key metrics and recent activity on the WasteNot platform
                </p>
              </div>
              <div className="px-4 py-5 sm:p-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
                <div>
                  <h4 className="text-base font-medium text-gray-900">Recent Activity</h4>
                  <ul className="mt-3 space-y-3">
                    <li className="bg-gray-50 p-3 rounded-md">
                      <p className="text-sm"><span className="font-medium">New donor registration:</span> Green Acres Farm</p>
                      <p className="text-xs text-gray-500 mt-1">Today, 09:45 AM</p>
                    </li>
                    <li className="bg-gray-50 p-3 rounded-md">
                      <p className="text-sm"><span className="font-medium">New food donation:</span> 15kg of fresh vegetables</p>
                      <p className="text-xs text-gray-500 mt-1">Yesterday, 02:30 PM</p>
                    </li>
                    <li className="bg-gray-50 p-3 rounded-md">
                      <p className="text-sm"><span className="font-medium">Certificate approved:</span> Nairobi Food Market</p>
                      <p className="text-xs text-gray-500 mt-1">Yesterday, 10:15 AM</p>
                    </li>
                  </ul>
                </div>
                
                <div>
                  <h4 className="text-base font-medium text-gray-900">Top Donors This Month</h4>
                  <ul className="mt-3 space-y-3">
                    <li className="flex justify-between items-center bg-gray-50 p-3 rounded-md">
                      <span className="text-sm">Green Acres Farm</span>
                      <span className="text-sm font-medium">48kg</span>
                    </li>
                    <li className="flex justify-between items-center bg-gray-50 p-3 rounded-md">
                      <span className="text-sm">Nairobi Food Market</span>
                      <span className="text-sm font-medium">36kg</span>
                    </li>
                    <li className="flex justify-between items-center bg-gray-50 p-3 rounded-md">
                      <span className="text-sm">Spice Garden Restaurant</span>
                      <span className="text-sm font-medium">29kg</span>
                    </li>
                  </ul>
                </div>
                
                <div>
                  <h4 className="text-base font-medium text-gray-900">Areas With Most Need</h4>
                  <ul className="mt-3 space-y-3">
                    <li className="flex justify-between items-center bg-gray-50 p-3 rounded-md">
                      <span className="text-sm">Kibera</span>
                      <span className="text-sm font-medium">12 requests</span>
                    </li>
                    <li className="flex justify-between items-center bg-gray-50 p-3 rounded-md">
                      <span className="text-sm">Mathare</span>
                      <span className="text-sm font-medium">9 requests</span>
                    </li>
                    <li className="flex justify-between items-center bg-gray-50 p-3 rounded-md">
                      <span className="text-sm">Kawangware</span>
                      <span className="text-sm font-medium">7 requests</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Requests Tab Content */}
        {activeTab === 'requests' && <AdminRequestsTab />}
        
        {/* Certificates Tab Content */}
        {activeTab === 'certificates' && <AdminCertificatesTab />}
        
        {/* Users Tab Content */}
        {activeTab === 'users' && <AdminUsersTab />}
        
        {/* Reports Tab Content */}
        {activeTab === 'reports' && <AdminReportsTab />}
        
        {/* Content Management Tab */}
        {activeTab === 'content' && <AdminContentTab />}

        {/* Listings Tab Content */}
        {activeTab === 'listings' && <AdminListingsTab />}

        {/* Certificate Verification Tab */}
        {activeTab === 'verification' && <AdminCertificateVerificationTab />}
      </div>
    </div>
  );
}

export default AdminDashboardPage;
